import React, { useEffect, useMemo, useRef, useState } from 'react'

import useSyncSetting from './useSyncSetting'
import useInterval from './useInterval'
import {
  collectOutOfStock,
  markInStock,
  formatRemaining,
  formatAge,
  displayName,
} from '../utils/outOfStock'
import './outOfStockBar.scss'

/*
  Shows what is currently out of stock so staff can put it back without walking
  over to the menu updater, and clears anything whose timer has run out.

  The sweep is a convenience, not the enforcement: the timestamp lives on the item
  itself so the ordering site can honor it whether or not a tablet is awake.
*/
export default function OutOfStockBar ({ shopId }) {
  const [menuRef, menuValue] = useSyncSetting(shopId, 'menu', false, {})
  const [now, setNow] = useState(Date.now())

  useInterval(() => { setNow(Date.now()) }, 60 * 1000)

  const items = useMemo(() => collectOutOfStock(menuValue, now), [menuValue, now])

  // useSyncSetting hands back a fresh ref object each render, so it can't be an
  // effect dependency without the sweep running on every render.
  const latestMenuRef = useRef(menuRef)
  latestMenuRef.current = menuRef

  const expiredIds = useMemo(
    () => items.filter((item) => item.expired).map((item) => item.itemId),
    [items]
  )
  const expiredKey = expiredIds.join(',')

  useEffect(() => {
    if (!expiredKey) { return }
    console.log('out of stock timers expired', expiredKey)
    expiredKey.split(',').forEach((itemId) => {
      markInStock(latestMenuRef.current, itemId)
    })
  }, [expiredKey])

  const live = items.filter((item) => !item.expired)
  if (live.length === 0) { return null }

  return (
    <div className="out-of-stock-bar">
      <div className="oos-bar-label">
        OUT OF STOCK
        <span className="oos-bar-count">{live.length}</span>
      </div>
      <div className="oos-bar-items">
        {live.map((item) => (
          <button
            key={item.itemId}
            className="oos-chip"
            data-state={item.forever ? 'forever' : item.until ? 'timed' : 'legacy'}
            title={`${item.name || item.itemId} — tap to put back in stock`}
            onClick={() => markInStock(menuRef, item.itemId)}
          >
            <span className="oos-chip-name">{displayName(item)}</span>
            <span className="oos-chip-time">
              {item.forever
                ? 'no end date'
                : item.until
                  ? formatRemaining(item.until, now)
                  : `no timer · ${formatAge(item.since, now)}`}
            </span>
            <span className="oos-chip-restore">✕</span>
          </button>
        ))}
      </div>
    </div>
  )
}
